import { toast } from "react-toastify";
import { ethers } from 'ethers';
import { showTransactionToast, showTxSuccess, showTxError } from "../../Components/Toast/TransactionToast";
import { setPerpsUpdate } from "../../ReduxSlices/TradeSlice";
import { PERP_VAULT_INTERFACE_ABI } from '../EvmConstants';
import {
  parseTradeIdFromReceipt,
  fetchEvmTokenBalance,
  fetchCombinedBalance,
  executeWithGasEstimation,
} from './tradingEvmService';
import { getCollateralDenom, getEvmExplorerUrl } from '../../config/network';
import { enforceClientOrigin } from '../../utils/clientOrigin';

// Helper function to get EVM transaction explorer URL (network-aware)
// Returns full URL for direct use
const getTransactionLink = (txHash) => {
  return getEvmExplorerUrl(txHash);
};

/**
 * EVM-only contract execution for general functions
 * @param {Object} dispatch - Redux dispatch function
 * @param {Object} CONTRACT_MESSAGE - Wasm message to execute
 * @param {string} account - User's EVM address
 * @param {Array} tokenToSend - Array of tokens to send (optional)
 * @returns {Promise<string|false>} Transaction hash or false
 */
export const executeEvmContract = async (
  dispatch,
  CONTRACT_MESSAGE,
  account,
  tokenToSend = null
) => {
  try {
    validateDispatch(dispatch);
    if (!window.ethereum) {
      toast.error("MetaMask is not installed");
      return false;
    }

    showTransactionToast("Placing order, just a second...");

    console.log("Using EVM wallet with account:", account);
    
    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    
    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      throw new Error("Missing REACT_APP_SAI_EVM_INTERFACE_ADDRESS environment variable");
    }

    // Create contract instance
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);

    // Mandatory: guarantee client_origin on every open_trade message, no matter
    // how the caller built it. No-op for non-open_trade messages.
    enforceClientOrigin(CONTRACT_MESSAGE);

    // Convert Wasm message to bytes
    const wasmMsgExecute = ethers.toUtf8Bytes(JSON.stringify(CONTRACT_MESSAGE));

    let hash;
    if (tokenToSend && tokenToSend.length > 0) {
      // Handle transactions that require funds
      const tokenAmount = parseFloat(tokenToSend[0].amount / 1000000); // Convert from micro-USDC to USDC
      
      console.log("Processing transaction with collateral:", {
        originalAmount: tokenToSend[0].amount,
        convertedAmount: tokenAmount,
        denom: tokenToSend[0].denom
      });

      // Convert amount to proper denomination (micro-USDC for contract)
      const totalCollateralAmount = ethers.parseUnits(tokenAmount.toString(), 6); // Total collateral needed
      const erc20CollateralAmount = totalCollateralAmount; // Use all from ERC20 for now
      
      console.log("Collateral configuration:", {
        totalCollateral: totalCollateralAmount.toString(),
        erc20Collateral: erc20CollateralAmount.toString(),
        bankCollateral: (totalCollateralAmount - erc20CollateralAmount).toString()
      });

      // Get collateral index from environment
      const collateralIndex = parseInt(process.env.REACT_APP_PERP_TOKEN_INDEXES_COLLATERAL?.replace(/[^\d]/g, '') || '3');

      // Execute with funds using openTrade function (gas estimated dynamically)
      const tx = await executeWithGasEstimation(contract, 'openTrade', [
        wasmMsgExecute,
        collateralIndex,           // collateralIndex (USDC = 3)
        totalCollateralAmount,     // totalCollateralAmount (total collateral needed)
        erc20CollateralAmount,     // erc20CollateralAmount (amount from ERC20)
      ]);

      hash = tx.hash;
      await tx.wait();
    } else {
      // Execute simple function without funds (gas estimated dynamically)
      const tx = await executeWithGasEstimation(contract, 'executeSimpleFunctions', [
        wasmMsgExecute,
      ]);

      hash = tx.hash;
      await tx.wait();
    }

    showTxSuccess(
      <div>
        Transaction confirmed!
        <br />
        <a
          href={getTransactionLink(hash)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:text-blue-700 underline"
        >
          View transaction
        </a>
      </div>
    );

    return hash;
  } catch (error) {
    const errorText = error.toString();
    console.log(error.toString());

    // EVM-specific error handling
    if (errorText.includes("user rejected")) {
      showTxError("Transaction was rejected by user");
    } else if (errorText.includes("insufficient funds")) {
      showTxError("Insufficient funds for gas fees");
    } else if (errorText.includes("network")) {
      showTxError("Network connection issue. Please check your wallet is connected to Nibiru EVM");
    } else if (errorText.includes("nonce")) {
      showTxError("Transaction nonce error. Please try again");
    } else {
      showTxError(`Transaction failed with following errors ${error.toString()}`);
    }
    return false;
  }
};

/**
 * EVM-only perp trading execution
 * @param {Object} dispatch - Redux dispatch function
 * @param {Object} CONTRACT_MESSAGE - Wasm message to execute
 * @param {string} account - User's EVM address
 * @param {Array} tokenToSend - Array of tokens to send
 * @returns {Promise<string|false>} Transaction hash or false
 */
export const executeEvmPerps = async (
  dispatch,
  CONTRACT_MESSAGE,
  account,
  tokenToSend
) => {
  try {
    validateDispatch(dispatch);
    if (!window.ethereum) {
      toast.error("MetaMask is not installed");
      return false;
    }

    console.log("Using EVM wallet for perps trading:", account);

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      throw new Error("Missing REACT_APP_SAI_EVM_INTERFACE_ADDRESS environment variable");
    }

    // Validate required parameters
    const microUsdcAmount = parseFloat(tokenToSend[0].amount); // This is already in micro-USDC
    const usdcAmount = microUsdcAmount / 1000000; // Convert to USDC for display and approval

    if (usdcAmount <= 0) {
      throw new Error("Trade amount must be greater than 0");
    }

    console.log("Trade amount validation:", {
      microUsdcAmount,
      usdcAmount,
      originalAmount: tokenToSend[0].amount
    });

    // Check combined ERC20 + bank USDC balance
    const bech32Addr = window.__nibiru_bech32 || null;

    const { erc20: erc20Bal, bank: bankBal, total: combinedBalance } =
      await fetchCombinedBalance(account, bech32Addr);
    console.log("Combined USDC balance:", { erc20: erc20Bal, bank: bankBal, total: combinedBalance });

    if (combinedBalance < usdcAmount) {
      toast.error(`Insufficient USDC balance. Required: ${usdcAmount}, Available: ${combinedBalance.toFixed(2)}`);
      return false;
    }

    showTransactionToast("Placing order, just a second...");

    // Create contract instance
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);

    // Mandatory: guarantee client_origin on every open_trade message, no matter
    // how the caller built it. No-op for non-open_trade messages.
    enforceClientOrigin(CONTRACT_MESSAGE);

    console.log("Executing perp trade with message:", JSON.stringify(CONTRACT_MESSAGE, null, 2));

    // Convert Wasm message to bytes
    const wasmMsgExecute = ethers.toUtf8Bytes(JSON.stringify(CONTRACT_MESSAGE));

    // Configure collateral amounts — smart split: ERC20 first, remainder from bank
    const totalCollateralAmount = ethers.parseUnits(usdcAmount.toString(), 6);
    const erc20Available = ethers.parseUnits(erc20Bal.toString(), 6);
    const erc20CollateralAmount = totalCollateralAmount <= erc20Available
      ? totalCollateralAmount
      : erc20Available;
    const bankCollateralAmount = totalCollateralAmount - erc20CollateralAmount;
    
    // Get collateral index from environment
    const collateralIndex = parseInt(process.env.REACT_APP_PERP_TOKEN_INDEXES_COLLATERAL?.replace(/[^\d]/g, '') || '3');
    
    console.log("Collateral configuration:", {
      collateralIndex,
      totalCollateral: totalCollateralAmount.toString(),
      erc20Collateral: erc20CollateralAmount.toString(),
      bankCollateral: bankCollateralAmount.toString(),
      difference: (totalCollateralAmount - erc20CollateralAmount).toString()
    });
    
    console.log("Final transaction parameters:", {
      wasmMessage: JSON.stringify(CONTRACT_MESSAGE),
      collateralIndex,
      totalCollateralAmount: totalCollateralAmount.toString(),
      erc20CollateralAmount: erc20CollateralAmount.toString(),
      contractAddress,
      account
    });
    
    // Execute the trade using openTrade function with proper collateral configuration
    // Gas is estimated dynamically; falls back to a generous static limit
    const tx = await executeWithGasEstimation(contract, 'openTrade', [
      wasmMsgExecute,           // Wasm message as bytes
      collateralIndex,          // collateralIndex (USDC = 3)
      totalCollateralAmount,    // totalCollateralAmount (total collateral needed)
      erc20CollateralAmount,    // erc20CollateralAmount (amount from ERC20)
    ]);
    
    console.log("Transaction sent:", tx.hash);
    
    // Wait for confirmation
    const receipt = await tx.wait();
    console.log("Transaction confirmed:", receipt);
    
    // Parse trade ID from receipt
    const tradeId = parseTradeIdFromReceipt(receipt);
    console.log("Parsed trade ID:", tradeId);

    showTxSuccess(
      <div>
        Trade opened successfully!
        <br />
        <a
          href={getTransactionLink(receipt.hash)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:text-blue-700 underline"
        >
          View transaction
        </a>
      </div>
    );
    dispatch(setPerpsUpdate());

    return receipt.hash;
  } catch (error) {
    const errorMessage = error.toString().toLowerCase();

    // EVM-specific error handling for perps
    let userMessage;
    if (errorMessage.includes("insufficient funds") || errorMessage.includes("out of gas")) {
      userMessage = "Insufficient balance to complete this transaction. Please add funds to your wallet.";
    } else if (errorMessage.includes("nonce") || errorMessage.includes("transaction count")) {
      userMessage = "Transaction nonce error. Please try again in a moment.";
    } else if (errorMessage.includes("chain id") || errorMessage.includes("network") || errorMessage.includes("wrong network")) {
      userMessage = "Network connection issue. Please check your wallet is connected to Nibiru EVM.";
    } else if (errorMessage.includes("rejected") || errorMessage.includes("declined") || errorMessage.includes("cancelled") || errorMessage.includes("user rejected")) {
      userMessage = "Transaction was declined. Please try again.";
    } else if (errorMessage.includes("timeout") || errorMessage.includes("timed out")) {
      userMessage = "Transaction timed out. The network might be congested, please try again.";
    } else if (errorMessage.includes("not connected") || errorMessage.includes("no provider")) {
      userMessage = "Wallet connection lost. Please reconnect your wallet and try again.";
    } else if (errorMessage.includes("slippage")) {
      userMessage = "Price changed during transaction. Try again with a higher slippage tolerance.";
    } else if (errorMessage.includes("limit") || errorMessage.includes("max")) {
      userMessage = "This transaction exceeds platform limits. Please try a smaller amount.";
    } else if (errorMessage.includes("insufficient allowance")) {
      userMessage = "USDC allowance insufficient. Please approve USDC spending and try again.";
    } else {
      userMessage = "Unable to process transaction. Please try again.";
    }

    showTxError(userMessage);
    console.error(`EVM Transaction error details: ${error.toString()}`);
    return false;
  }
};

/**
 * Query EVM contract (read-only operations)
 * @param {string} contractAddr - Contract address
 * @param {Object} CONTRACT_MESSAGE - Query message
 * @returns {Promise<any>} Query result
 */
export const queryEvmContract = async (contractAddr, CONTRACT_MESSAGE) => {
  try {
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const contract = new ethers.Contract(contractAddr, PERP_VAULT_INTERFACE_ABI, provider);
    
    // Convert message to bytes for query
    const wasmMsgQuery = ethers.toUtf8Bytes(JSON.stringify(CONTRACT_MESSAGE));
    
    // Execute query (this would need a query function in the contract)
    // Note: This assumes the contract has a query function
    const result = await contract.queryContract(wasmMsgQuery);
    
    return result;
  } catch (error) {
    console.error("EVM contract query error:", error);
    throw error;
  }
};

/**
 * Get EVM token balance (USDC)
 * @param {string} account - User's EVM address
 * @returns {Promise<Object>} Balance object
 */
export const queryEvmBalance = async (account) => {
  try {
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const usdcContractAddress = process.env.REACT_APP_NIBIRU_USDC_CONTRACT_ADDRESS;
    
    if (!usdcContractAddress) {
      throw new Error("USDC contract address not configured");
    }

    // Get USDC balance
    const balance = await provider.call({
      to: usdcContractAddress,
      data: ethers.concat([
        ethers.id("balanceOf(address)").slice(0, 10),
        ethers.AbiCoder.defaultAbiCoder().encode(["address"], [account])
      ])
    });

    const balanceDecoded = ethers.AbiCoder.defaultAbiCoder().decode(["uint256"], balance)[0];
    const formattedBalance = ethers.formatUnits(balanceDecoded, 6); // USDC has 6 decimals
    
    return {
      amount: (Number(formattedBalance) * 1000000).toString(), // Convert to micro-USDC for consistency
      denom: getCollateralDenom() || "usdc" // Use network-aware denom or fallback
    };
  } catch (error) {
    console.error("Error fetching EVM balance:", error);
    return { amount: "0", denom: process.env.REACT_APP_COLLATERAL_DENOM || "usdc" };
  }
};

/**
 * Get current price from EVM oracle (if available)
 * Note: This might need to be implemented based on available oracle functions
 * @returns {Promise<number|null>} Current price or null
 */
export const getEvmCurrentPrice = async () => {
  try {
    // For now, this might need to fall back to the Cosmos oracle
    // or implement EVM-specific oracle queries
    console.log("EVM oracle price query not yet implemented");
    return null;
  } catch (err) {
    console.error("Failed to fetch current price from EVM oracle:", err);
    return null;
  }
};

/**
 * Fetches all trades for a given EVM account
 * Note: This would require the contract to have trade query functions
 * @param {string} account - EVM account address
 * @returns {Promise<Array>} Array of trades
 */
export const getEvmTrades = async (account) => {
  try {
    console.log("EVM trades query not yet implemented for account:", account);
    // This would need to be implemented based on available contract functions
    return [];
  } catch (err) {
    console.error("Failed to fetch EVM trades:", err);
    return [];
  }
};

/**
 * Utility function to convert Cosmos address to EVM address (if needed)
 * @param {string} cosmosAddress - Cosmos bech32 address
 * @returns {string} EVM hex address
 */
export const cosmosToEvmAddress = (cosmosAddress) => {
  try {
    // This would need proper address conversion logic
    // For now, return as-is if it's already EVM format
    if (cosmosAddress.startsWith('0x')) {
      return cosmosAddress;
    }
    
    // Implement proper conversion if needed
    console.warn("Cosmos to EVM address conversion not implemented");
    return cosmosAddress;
  } catch (error) {
    console.error("Error converting address:", error);
    return cosmosAddress;
  }
};

// In-memory cache for EVM → bech32 conversions
const _bech32Cache = {};

/**
 * Convert EVM address to Cosmos bech32 address.
 * Uses in-memory cache + window.__nibiru_bech32 to avoid repeated precompile calls.
 * @param {string} evmAddress - EVM hex address (0x...)
 * @returns {Promise<string>} Cosmos bech32 address (nibi...)
 */
export const evmToBech32Address = async (evmAddress) => {
  try {
    if (!evmAddress || !evmAddress.startsWith('0x')) {
      throw new Error("Invalid EVM address format");
    }

    const key = evmAddress.toLowerCase();

    // Check cache first (instant)
    if (_bech32Cache[key]) return _bech32Cache[key];

    // Pure, offline conversion: a Nibiru EVM account's bech32 address is simply the
    // bech32("nibi") encoding of the 20-byte EVM address. This is identical to what
    // the funtoken precompile's whoAmI returns, but needs NO on-chain call — so it
    // works regardless of which EVM chain the wallet is currently on. The previous
    // implementation called the precompile via window.ethereum, which failed whenever
    // the wallet wasn't on Nibiru EVM (or the RPC hiccuped), silently returning no
    // bech32 address and breaking BOTH balance and trade fetching (which both depend
    // on this conversion).
    const { toBech32 } = require("@cosmjs/encoding");
    const addrBytes = ethers.getBytes(ethers.getAddress(evmAddress));
    const bech32Address = toBech32("nibi", addrBytes);

    if (!bech32Address || !bech32Address.startsWith('nibi')) {
      throw new Error(`Invalid bech32 address computed: ${bech32Address}`);
    }

    // Cache for future calls
    _bech32Cache[key] = bech32Address;
    window.__nibiru_bech32 = bech32Address;
    return bech32Address;
  } catch (error) {
    console.error("Error converting EVM to bech32 address:", error.message);
    throw new Error(`Failed to convert address: ${error.message}`);
  }
};


/**
 * Convert Nibiru bech32 address to EVM hex address (pure math, no RPC).
 * @param {string} bech32Addr - Nibiru bech32 address (nibi1...)
 * @returns {string} EVM hex address (0x...)
 */
export const bech32ToEvmAddress = (bech32Addr) => {
  try {
    const { fromBech32 } = require("@cosmjs/encoding");
    const { data } = fromBech32(bech32Addr);
    return ethers.hexlify(data);
  } catch (err) {
    console.warn("bech32 to EVM conversion failed:", err.message);
    return null;
  }
};

/**
 * Get bech32 address for current EVM account
 * @returns {Promise<string>} Cosmos bech32 address
 */
export const getCurrentBech32Address = async () => {
  try {
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const evmAddress = await signer.getAddress();
    
    return await evmToBech32Address(evmAddress);
  } catch (error) {
    console.error("Error getting current bech32 address:", error);
    throw error;
  }
};

/**
 * Check if current network is Nibiru EVM
 * @returns {Promise<boolean>} True if on correct network
 */
export const checkEvmNetwork = async () => {
  try {
    if (!window.ethereum) {
      return false;
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const network = await provider.getNetwork();
    const expectedChainId = process.env.REACT_APP_NIBIRU_EVM_CHAIN_ID;
    
    return network.chainId.toString() === parseInt(expectedChainId, 16).toString();
  } catch (error) {
    console.error("Error checking EVM network:", error);
    return false;
  }
};

/**
 * Switch to Nibiru EVM network
 * @returns {Promise<boolean>} True if switch was successful
 */
export const switchToNibiruEvm = async () => {
  try {
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }

    const chainId = process.env.REACT_APP_NIBIRU_EVM_CHAIN_ID;

    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: chainId }],
    });

    return true;
  } catch (switchError) {
    // This error code indicates that the chain has not been added to MetaMask
    if (switchError.code === 4902) {
      try {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: process.env.REACT_APP_NIBIRU_EVM_CHAIN_ID,
              chainName: 'Nibiru EVM Mainnet',
              rpcUrls: [process.env.REACT_APP_NIBIRU_EVM_RPC],
              nativeCurrency: {
                name: 'NIBI',
                symbol: 'NIBI',
                decimals: 18,
              },
              blockExplorerUrls: ['https://evm-rpc.nibiru.fi'],
            },
          ],
        });
        return true;
      } catch (addError) {
        console.error("Error adding Nibiru EVM network:", addError);
        return false;
      }
    }
    console.error("Error switching to Nibiru EVM network:", switchError);
    return false;
  }
};

// Add dispatch validation to other functions
const validateDispatch = (dispatch) => {
  if (!dispatch || typeof dispatch !== 'function') {
    throw new Error("Invalid dispatch function provided");
  }
};